import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Archive,
  FilePlus2,
  FolderKanban,
  Link2,
  Loader2,
  Plus,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/api";
import {
  addMaterialidadProjectProvider,
  archiveMaterialidadProject,
  createMaterialidadProject,
  fetchMaterialidadProject,
  fetchMaterialidadProjectCfdiCandidates,
  fetchMaterialidadProjects,
  linkMaterialidadProjectCfdi,
  materialidadProjectDocumentFileUrl,
  removeMaterialidadProjectProvider,
  unlinkMaterialidadProjectCfdi,
  uploadMaterialidadProjectDocument,
} from "@/modules/tramites/materialidad/materialidadProjectsApi";

const STATUS_LABELS = {
  open: "Abierto",
  en_revision: "En revisión",
  cerrado: "Cerrado",
};

const ROLE_LABELS = {
  contratista: "Contratista",
  materiales: "Materiales",
  subcontrato: "Subcontrato",
  servicios: "Servicios",
  otro: "Otro",
};

function money(n) {
  const v = Number(n || 0);
  return v.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

function clientLabel(cli) {
  if (!cli) return "—";
  return cli.razon_social || cli.nombre_comercial || cli.rfc || cli.id;
}

/**
 * Pestaña Proyectos — expediente de obra (MVP).
 * Aislado de Faltantes / Portal / SAT.
 */
export default function MaterialidadProyectosPanel({ clientId, clients = [] }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ document_types: [], provider_roles: [] });
  const [filterClientId, setFilterClientId] = useState(clientId || "");
  const [companySearch, setCompanySearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    code: "",
    date_start: "",
    date_end: "",
    amount: "",
    location: "",
    description: "",
  });
  const [providerForm, setProviderForm] = useState({ rfc: "", name: "", role: "otro" });
  const [cfdiUuid, setCfdiUuid] = useState("");
  const [cfdiProviderId, setCfdiProviderId] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [docType, setDocType] = useState("contrato");
  const [docProviderId, setDocProviderId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (clientId) setFilterClientId(clientId);
  }, [clientId]);

  const clientsById = useMemo(() => {
    const map = new Map();
    for (const c of clients || []) {
      if (c?.id) map.set(c.id, c);
    }
    return map;
  }, [clients]);

  const companySearchNeedle = companySearch.trim().toLowerCase();

  const clientsForSelect = useMemo(() => {
    const rows = Array.isArray(clients) ? clients : [];
    if (!companySearchNeedle) return rows;
    return rows.filter((c) => {
      const label = clientLabel(c).toLowerCase();
      const rfc = String(c?.rfc || "").toLowerCase();
      return label.includes(companySearchNeedle) || rfc.includes(companySearchNeedle);
    });
  }, [clients, companySearchNeedle]);

  const matchingClientIds = useMemo(() => {
    if (!companySearchNeedle) return null;
    return new Set(clientsForSelect.map((c) => c.id));
  }, [companySearchNeedle, clientsForSelect]);

  const visibleItems = useMemo(() => {
    if (!matchingClientIds) return items;
    if (filterClientId) return items;
    return items.filter((row) => matchingClientIds.has(row.client_id));
  }, [items, matchingClientIds, filterClientId]);

  const reloadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMaterialidadProjects({
        clientId: filterClientId || undefined,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setMeta({
        document_types: data?.document_types || [],
        provider_roles: data?.provider_roles || [],
      });
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudieron cargar proyectos");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filterClientId]);

  useEffect(() => {
    reloadList();
  }, [reloadList]);

  const openDetail = async (projectId) => {
    setSelectedId(projectId);
    setDetailLoading(true);
    try {
      const data = await fetchMaterialidadProject(projectId);
      setDetail(data);
      const types = data?.document_types || meta.document_types || [];
      if (types.length && !types.some((t) => t.id === docType)) {
        setDocType(types[0].id);
      }
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo abrir el proyecto");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (!selectedId) return;
    const data = await fetchMaterialidadProject(selectedId);
    setDetail(data);
    await reloadList();
  };

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    const cid = filterClientId || clientId;
    if (!cid) {
      toast.error("Seleccione una empresa (cliente) en la barra superior o en el filtro");
      return;
    }
    if (!createForm.name.trim()) {
      toast.error("Indique el nombre del proyecto");
      return;
    }
    setBusy(true);
    try {
      const created = await createMaterialidadProject({
        client_id: cid,
        name: createForm.name.trim(),
        code: createForm.code.trim() || undefined,
        date_start: createForm.date_start || undefined,
        date_end: createForm.date_end || undefined,
        amount: createForm.amount ? Number(createForm.amount) : 0,
        location: createForm.location.trim() || undefined,
        description: createForm.description.trim() || undefined,
      });
      toast.success("Proyecto creado");
      setShowCreate(false);
      setCreateForm({
        name: "",
        code: "",
        date_start: "",
        date_end: "",
        amount: "",
        location: "",
        description: "",
      });
      await reloadList();
      await openDetail(created.id);
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo crear el proyecto");
    } finally {
      setBusy(false);
    }
  };

  const handleAddProvider = async (e) => {
    e?.preventDefault?.();
    if (!detail?.id) return;
    setBusy(true);
    try {
      const data = await addMaterialidadProjectProvider(detail.id, {
        rfc: providerForm.rfc.trim().toUpperCase(),
        name: providerForm.name.trim() || undefined,
        role: providerForm.role || "otro",
      });
      setDetail(data);
      setProviderForm({ rfc: "", name: "", role: "otro" });
      toast.success("Proveedor agregado");
      await reloadList();
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo agregar el proveedor");
    } finally {
      setBusy(false);
    }
  };

  const handleLinkCfdi = async () => {
    if (!detail?.id || !cfdiUuid.trim()) return;
    setBusy(true);
    try {
      const data = await linkMaterialidadProjectCfdi(detail.id, {
        uuid: cfdiUuid.trim(),
        providerId: cfdiProviderId || undefined,
      });
      setDetail(data);
      setCfdiUuid("");
      toast.success("CFDI vinculado");
      await reloadList();
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo vincular el CFDI");
    } finally {
      setBusy(false);
    }
  };

  const handleSearchCandidates = async () => {
    if (!detail?.id) return;
    try {
      const data = await fetchMaterialidadProjectCfdiCandidates(detail.id, {
        q: cfdiUuid.trim() || undefined,
        rfc: providerForm.rfc.trim() || undefined,
        limit: 20,
      });
      setCandidates(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudieron buscar CFDI");
    }
  };

  const handleUploadDoc = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !detail?.id) return;
    setBusy(true);
    try {
      await uploadMaterialidadProjectDocument(detail.id, {
        file,
        documentType: docType,
        providerId: docProviderId || undefined,
      });
      toast.success("Documento subido");
      await refreshDetail();
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo subir el documento");
    } finally {
      setBusy(false);
      if (e?.target) e.target.value = "";
    }
  };

  return (
    <div className="space-y-4" data-testid="materialidad-proyectos-panel">
      <div className="rounded-sm border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3 justify-between">
          <div className="flex flex-wrap items-end gap-3 flex-1 min-w-0">
            <div className="space-y-1 min-w-[200px] max-w-xs flex-1">
              <Label className="text-xs text-slate-600">Empresa del proyecto</Label>
              <Select
                value={filterClientId || "__all__"}
                onValueChange={(v) => setFilterClientId(v === "__all__" ? "" : v)}
              >
                <SelectTrigger className="h-9 rounded-sm" data-testid="materialidad-proyectos-client-filter">
                  <SelectValue placeholder="Todas / seleccione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas las empresas</SelectItem>
                  {clientsForSelect.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {clientLabel(c)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {companySearchNeedle && clientsForSelect.length === 0 ? (
                <p className="text-[10px] text-amber-800 m-0">Ninguna empresa coincide con la búsqueda.</p>
              ) : null}
            </div>
            <div className="space-y-1 min-w-[200px] max-w-sm flex-1">
              <Label className="text-xs text-slate-600" htmlFor="materialidad-proyectos-company-search">
                Buscar empresa
              </Label>
              <div className="relative">
                <Search
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none"
                  aria-hidden
                />
                <Input
                  id="materialidad-proyectos-company-search"
                  className="h-9 rounded-sm pl-8"
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  placeholder="Nombre o RFC…"
                  data-testid="materialidad-proyectos-company-search"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-sm text-xs"
              onClick={reloadList}
              disabled={loading}
              data-testid="materialidad-proyectos-refresh"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Actualizar"}
            </Button>
            <Button
              type="button"
              className="h-9 rounded-sm text-xs bg-[#0F2D5C] hover:bg-[#0a2247]"
              onClick={() => setShowCreate((v) => !v)}
              data-testid="materialidad-proyectos-new-toggle"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Nuevo proyecto
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 m-0">
          Expediente por obra: proveedores, CFDI del Visor y documentos. Vive dentro de Portal de clientes; no afecta descarga SAT ni la revisión del despacho.
        </p>
      </div>

      {showCreate ? (
        <form
          onSubmit={handleCreate}
          className="rounded-sm border border-[#0F2D5C]/20 bg-[#0F2D5C]/[0.03] p-4 space-y-3"
          data-testid="materialidad-proyectos-create-form"
        >
          <p className="text-sm font-semibold text-[#0F2D5C] m-0 flex items-center gap-2">
            <FolderKanban className="w-4 h-4" aria-hidden />
            Alta de proyecto
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs">Nombre *</Label>
              <Input
                className="h-9 rounded-sm mt-1"
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="materialidad-proyectos-name"
              />
            </div>
            <div>
              <Label className="text-xs">Clave</Label>
              <Input
                className="h-9 rounded-sm mt-1"
                value={createForm.code}
                onChange={(e) => setCreateForm((f) => ({ ...f, code: e.target.value }))}
                placeholder="OBR-2026-014"
              />
            </div>
            <div>
              <Label className="text-xs">Monto / presupuesto</Label>
              <Input
                type="number"
                step="0.01"
                className="h-9 rounded-sm mt-1"
                value={createForm.amount}
                onChange={(e) => setCreateForm((f) => ({ ...f, amount: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Fecha inicio</Label>
              <Input
                type="date"
                className="h-9 rounded-sm mt-1"
                value={createForm.date_start}
                onChange={(e) => setCreateForm((f) => ({ ...f, date_start: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Fecha fin</Label>
              <Input
                type="date"
                className="h-9 rounded-sm mt-1"
                value={createForm.date_end}
                onChange={(e) => setCreateForm((f) => ({ ...f, date_end: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Ubicación</Label>
              <Input
                className="h-9 rounded-sm mt-1"
                value={createForm.location}
                onChange={(e) => setCreateForm((f) => ({ ...f, location: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Descripción / alcance</Label>
              <Textarea
                className="rounded-sm mt-1 text-sm"
                rows={2}
                value={createForm.description}
                onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy} className="h-9 rounded-sm text-xs bg-[#0F2D5C] hover:bg-[#0a2247]">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Guardar proyecto"}
            </Button>
            <Button type="button" variant="outline" className="h-9 rounded-sm text-xs" onClick={() => setShowCreate(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 rounded-sm border border-slate-200 bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-100 text-xs font-semibold text-slate-700">
            Proyectos ({visibleItems.length})
          </div>
          {loading ? (
            <p className="p-4 text-xs text-slate-500 flex items-center gap-2 m-0">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando…
            </p>
          ) : visibleItems.length === 0 ? (
            <p className="p-4 text-xs text-slate-500 m-0">
              {companySearchNeedle && !filterClientId
                ? "Ningún proyecto de empresas que coincidan con la búsqueda."
                : "Sin proyectos. Cree el primero con «Nuevo proyecto»."}
            </p>
          ) : (
            <ul className="m-0 p-0 list-none divide-y divide-slate-100 max-h-[28rem] overflow-y-auto">
              {visibleItems.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 ${
                      selectedId === row.id ? "bg-sky-50/80" : ""
                    }`}
                    onClick={() => openDetail(row.id)}
                    data-testid={`materialidad-proyecto-row-${row.id}`}
                  >
                    <p className="m-0 text-sm font-semibold text-[#0F2D5C] truncate">{row.name}</p>
                    <p className="m-0 text-[10px] text-slate-500 truncate">
                      {clientLabel(clientsById.get(row.client_id))}
                      {row.code ? ` · ${row.code}` : ""}
                    </p>
                    <p className="m-0 mt-1 text-[10px] text-slate-600 flex flex-wrap gap-x-2">
                      <span>{STATUS_LABELS[row.status] || row.status}</span>
                      <span>{row.providers_count || 0} prov.</span>
                      <span>{row.cfdi_linked_count || 0} CFDI</span>
                      <span>{row.documents_count || 0} docs</span>
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-3 rounded-sm border border-slate-200 bg-white p-4 min-h-[20rem]">
          {!selectedId ? (
            <p className="text-xs text-slate-500 m-0">Seleccione un proyecto para ver proveedores, CFDI y documentos.</p>
          ) : detailLoading ? (
            <p className="text-xs text-slate-500 m-0 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando detalle…
            </p>
          ) : !detail ? (
            <p className="text-xs text-rose-700 m-0">No se pudo cargar el proyecto.</p>
          ) : (
            <div className="space-y-5" data-testid="materialidad-proyecto-detail">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="text-base font-semibold text-[#0F2D5C] m-0">{detail.name}</h3>
                  <p className="text-[11px] text-slate-500 m-0 mt-0.5">
                    {STATUS_LABELS[detail.status] || detail.status}
                    {detail.date_start ? ` · ${detail.date_start}` : ""}
                    {detail.date_end ? ` → ${detail.date_end}` : ""}
                    {detail.amount ? ` · Presupuesto ${money(detail.amount)}` : ""}
                  </p>
                  <p className="text-[11px] text-slate-600 m-0 mt-1">
                    CFDI vinculados: {money(detail.cfdi_linked_total || 0)}
                    {detail.amount_delta != null ? ` · Δ vs presupuesto ${money(detail.amount_delta)}` : ""}
                  </p>
                </div>
                {detail.status !== "cerrado" ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 rounded-sm text-[10px]"
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        const data = await archiveMaterialidadProject(detail.id);
                        setDetail(data);
                        toast.success("Proyecto cerrado");
                        await reloadList();
                      } catch (err) {
                        toast.error(formatApiError(err) || "No se pudo cerrar");
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    <Archive className="w-3 h-3 mr-1" />
                    Cerrar proyecto
                  </Button>
                ) : null}
              </div>

              {/* Proveedores */}
              <section className="space-y-2">
                <p className="text-xs font-semibold text-slate-800 m-0">Proveedores</p>
                <ul className="m-0 p-0 list-none space-y-1.5">
                  {(detail.providers || []).map((p) => (
                    <li
                      key={p.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-100 bg-slate-50/80 px-2 py-1.5 text-[11px]"
                    >
                      <span>
                        <span className="font-mono font-semibold">{p.rfc}</span>
                        {p.name ? ` · ${p.name}` : ""}
                        <span className="text-slate-500"> · {ROLE_LABELS[p.role] || p.role}</span>
                      </span>
                      <button
                        type="button"
                        className="text-rose-700 hover:underline inline-flex items-center gap-1"
                        onClick={async () => {
                          setBusy(true);
                          try {
                            const data = await removeMaterialidadProjectProvider(detail.id, p.id);
                            setDetail(data);
                            await reloadList();
                          } catch (err) {
                            toast.error(formatApiError(err) || "No se pudo quitar");
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        <Trash2 className="w-3 h-3" /> Quitar
                      </button>
                    </li>
                  ))}
                  {(detail.providers || []).length === 0 ? (
                    <li className="text-[11px] text-slate-500">Sin proveedores aún.</li>
                  ) : null}
                </ul>
                <form onSubmit={handleAddProvider} className="flex flex-wrap gap-2 items-end">
                  <div>
                    <Label className="text-[10px]">RFC</Label>
                    <Input
                      className="h-8 rounded-sm mt-0.5 w-36 font-mono text-xs"
                      value={providerForm.rfc}
                      onChange={(e) => setProviderForm((f) => ({ ...f, rfc: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-[10px]">Nombre</Label>
                    <Input
                      className="h-8 rounded-sm mt-0.5 w-40 text-xs"
                      value={providerForm.name}
                      onChange={(e) => setProviderForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px]">Rol</Label>
                    <select
                      className="mt-0.5 h-8 rounded-sm border border-input bg-background px-2 text-xs"
                      value={providerForm.role}
                      onChange={(e) => setProviderForm((f) => ({ ...f, role: e.target.value }))}
                    >
                      {(meta.provider_roles?.length
                        ? meta.provider_roles
                        : Object.keys(ROLE_LABELS)
                      ).map((r) => (
                        <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                      ))}
                    </select>
                  </div>
                  <Button type="submit" disabled={busy} className="h-8 rounded-sm text-[10px] bg-[#0F2D5C] hover:bg-[#0a2247]">
                    <Plus className="w-3 h-3 mr-1" /> Agregar
                  </Button>
                </form>
              </section>

              {/* CFDI */}
              <section className="space-y-2">
                <p className="text-xs font-semibold text-slate-800 m-0 flex items-center gap-1">
                  <Link2 className="w-3.5 h-3.5" /> CFDI vinculados
                </p>
                <ul className="m-0 p-0 list-none space-y-1.5 max-h-40 overflow-y-auto">
                  {(detail.cfdi_links || []).map((link) => (
                    <li
                      key={link.uuid}
                      className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-100 px-2 py-1.5 text-[11px]"
                    >
                      <span className="min-w-0">
                        <span className="font-mono text-[10px] break-all">{link.uuid}</span>
                        <span className="block text-slate-600">
                          {link.fecha || "—"} · {money(link.total)} · {link.rfc_emisor || ""}
                        </span>
                      </span>
                      <button
                        type="button"
                        className="text-rose-700 hover:underline text-[10px]"
                        onClick={async () => {
                          setBusy(true);
                          try {
                            const data = await unlinkMaterialidadProjectCfdi(detail.id, link.uuid);
                            setDetail(data);
                            await reloadList();
                          } catch (err) {
                            toast.error(formatApiError(err) || "No se pudo desvincular");
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        Quitar
                      </button>
                    </li>
                  ))}
                  {(detail.cfdi_links || []).length === 0 ? (
                    <li className="text-[11px] text-slate-500">Ningún CFDI vinculado.</li>
                  ) : null}
                </ul>
                <div className="flex flex-wrap gap-2 items-end">
                  <div className="flex-1 min-w-[180px]">
                    <Label className="text-[10px]">UUID CFDI</Label>
                    <Input
                      className="h-8 rounded-sm mt-0.5 font-mono text-[10px]"
                      value={cfdiUuid}
                      onChange={(e) => setCfdiUuid(e.target.value)}
                      placeholder="Pegue UUID del Visor"
                      data-testid="materialidad-proyecto-cfdi-uuid"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px]">Proveedor (opc.)</Label>
                    <select
                      className="mt-0.5 h-8 rounded-sm border border-input bg-background px-2 text-xs max-w-[140px]"
                      value={cfdiProviderId}
                      onChange={(e) => setCfdiProviderId(e.target.value)}
                    >
                      <option value="">—</option>
                      {(detail.providers || []).map((p) => (
                        <option key={p.id} value={p.id}>{p.rfc}</option>
                      ))}
                    </select>
                  </div>
                  <Button
                    type="button"
                    disabled={busy || !cfdiUuid.trim()}
                    className="h-8 rounded-sm text-[10px] bg-[#0F2D5C] hover:bg-[#0a2247]"
                    onClick={handleLinkCfdi}
                    data-testid="materialidad-proyecto-link-cfdi"
                  >
                    Vincular
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 rounded-sm text-[10px]"
                    onClick={handleSearchCandidates}
                  >
                    Buscar en Visor
                  </Button>
                </div>
                {candidates.length > 0 ? (
                  <ul className="m-0 p-0 list-none space-y-1 max-h-32 overflow-y-auto border border-slate-100 rounded-sm p-2">
                    {candidates.map((c) => (
                      <li key={c.uuid} className="flex justify-between gap-2 text-[10px]">
                        <span className="font-mono truncate">{c.uuid}</span>
                        <button
                          type="button"
                          className="text-[#0F2D5C] underline shrink-0"
                          onClick={() => setCfdiUuid(c.uuid)}
                        >
                          Usar
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </section>

              {/* Documentos */}
              <section className="space-y-2">
                <p className="text-xs font-semibold text-slate-800 m-0 flex items-center gap-1">
                  <FilePlus2 className="w-3.5 h-3.5" /> Documentos
                </p>
                <ul className="m-0 p-0 list-none space-y-1.5">
                  {(detail.documents || []).map((doc) => (
                    <li
                      key={doc.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-100 px-2 py-1.5 text-[11px]"
                    >
                      <span>
                        <span className="font-medium">{doc.document_type_label || doc.document_type}</span>
                        <span className="text-slate-500"> · {doc.filename}</span>
                      </span>
                      <a
                        className="text-[#0F2D5C] underline text-[10px]"
                        href={materialidadProjectDocumentFileUrl(detail.id, doc.id)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Descargar
                      </a>
                    </li>
                  ))}
                  {(detail.documents || []).length === 0 ? (
                    <li className="text-[11px] text-slate-500">Sin documentos subidos.</li>
                  ) : null}
                </ul>
                <div className="flex flex-wrap gap-2 items-end">
                  <div>
                    <Label className="text-[10px]">Tipo</Label>
                    <select
                      className="mt-0.5 h-8 rounded-sm border border-input bg-background px-2 text-xs"
                      value={docType}
                      onChange={(e) => setDocType(e.target.value)}
                    >
                      {(detail.document_types || meta.document_types || []).map((t) => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px]">Proveedor (opc.)</Label>
                    <select
                      className="mt-0.5 h-8 rounded-sm border border-input bg-background px-2 text-xs max-w-[140px]"
                      value={docProviderId}
                      onChange={(e) => setDocProviderId(e.target.value)}
                    >
                      <option value="">Proyecto</option>
                      {(detail.providers || []).map((p) => (
                        <option key={p.id} value={p.id}>{p.rfc}</option>
                      ))}
                    </select>
                  </div>
                  <label className="inline-flex items-center gap-1.5 h-8 px-2 rounded-sm border border-slate-200 bg-white text-[10px] font-medium cursor-pointer hover:bg-slate-50">
                    <Upload className="w-3 h-3" />
                    Subir archivo
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleUploadDoc}
                      disabled={busy}
                      data-testid="materialidad-proyecto-upload"
                    />
                  </label>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
